import "./PersonalDetailsFormFields.scss";

import { RadioGroup } from "@headlessui/react";
import clsx from "clsx";
import { FC, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  Controller,
  Path,
  useFormContext,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import SelectedCheckbox from "@/assets/ux/SelectedCheckbox.svg?react";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { DateInput } from "@/components/Interface/FormFields/DateInput/DateInput";
import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { FormFieldWithHint } from "@/components/Interface/FormFields/FormField/FormFieldWithHintProps.tsx";
import { PhoneInputField } from "@/components/Interface/FormFields/PhoneInputField/PhoneInputField";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import type { Country } from "@/constants/countries";
import { SCHWEIZ } from "@/constants/countries";
import { useGetSortedCountries } from "@/hooks/useGetSortedCountries";
import { DataContext } from "@/provider/DataContextProvider";
import { useGetCustomer } from "@/services/api";
import { LookupValue } from "@/services/model";
import {
  DACH_HOUSE_NUMBER_REGEX,
  DACH_STREET_NAME_REGEX,
  DEFAULT_TEXT_FIELD_REGEX,
  getPostalCodePattern,
  INTERNATIONAL_NAME_REGEX,
} from "@/utils/constants";
import { getLocalizedBirthday, validateBirthDate } from "@/utils/customerUtils";
import { uidIsValid } from "@/utils/uidChecksum/uidChecksum";

const LEGAL_FORMS = [
  { id: 1, description: "Einzelfirma" },
  { id: 2, description: "Einfache Gesellschaft" },
  { id: 3, description: "Kollektivgesellschaft" },
  { id: 4, description: "Kommanditgesellschaft" },
  { id: 5, description: "AG" },
  { id: 6, description: "GmbH" },
  { id: 7, description: "Kommandit-AG" },
  { id: 8, description: "Genossenschaft" },
  { id: 9, description: "Verein" },
  { id: 10, description: "Stiftung" },
  { id: 11, description: "Öffentlich-rechtliche Körperschaft" },
];

const requireUIDCheck = [
  "AG",
  "GmbH",
  "Kommandit-AG",
  "Genossenschaft",
  "Verein",
  "Stiftung",
];

const doesRequireUIDCheck = (value: LookupValue) =>
  requireUIDCheck.some(
    (element) =>
      value && value.description && value.description.includes(element),
  );

export enum FormOfAddresses {
  "other",
  "mr",
  "mrs",
}

export enum PersonalDetailsFormFieldKeys {
  formOfAddress = "formOfAddress",
  firstName = "firstName",
  lastName = "lastName",
  street = "street",
  houseNumber = "houseNumber",
  postalDeliveryNotes = "postalDeliveryNotes",
  zip = "zip",
  city = "city",
  country = "country",
  phone = "phone",
  dateOfBirth = "dateOfBirth",
}

export enum BusinessPersonalDetailsFormFieldKeys {
  organizationName = "organizationName",
  organizationForm = "organizationForm",
  organizationId = "organizationId",
}

// Form keys combined in a const with a short name
export const fk = {
  ...PersonalDetailsFormFieldKeys,
  ...BusinessPersonalDetailsFormFieldKeys,
};
// For private forms, exclude the business fields
export const excludedFieldsPrivate = [
  fk.organizationName,
  fk.organizationForm,
  fk.organizationId,
];
//For the business form, no fields are excluded (personal details are now required for all customers)
export const excludedFieldsBusiness = [];

export type PersonalDetailsFormFieldsType = {
  [fk.formOfAddress]: string;
  [fk.firstName]: string;
  [fk.lastName]: string;
  [fk.street]: string;
  [fk.houseNumber]: string;
  [fk.postalDeliveryNotes]?: string;
  [fk.zip]: string;
  [fk.city]: string;
  [fk.country]: Country;
  [fk.phone]: string;
  [fk.dateOfBirth]: string;
};

export type BusinessPersonalDetailsFormFields =
  PersonalDetailsFormFieldsType & {
    [fk.organizationName]: string;
    [fk.organizationForm]: LookupValue;
    [fk.organizationId]: string;
  };

type PersonalDetailsFormFieldsProps = {
  disableAllFields?: boolean;
  excludedFields?: Path<BusinessPersonalDetailsFormFields>[];
  readonlyFields?: Path<BusinessPersonalDetailsFormFields>[];
  /** Heading above street/address fields (Benutzerkonto only). */
  addressHeading?: ReactNode;
  /** "top": org fields between personal and address, always shown (Benutzerkonto business edit).
   * "bottom" (default): Onboarding's checkbox-toggle after address. */
  organizationFieldsPosition?: "top" | "bottom";
  /** Heading above org fields when position="top". */
  organizationFieldsHeading?: ReactNode;
  /** Hides the "use this account for business" checkbox (Benutzerkonto only). */
  hideBusinessToggle?: boolean;
  /** Return false to prevent toggling the business section (e.g. FWB tribe warning). */
  onBusinessSectionToggle?: (checked: boolean) => boolean | void;
  /**
   * `"stacked"` = portal edit density: error messages rendered outside the grey
   * box and side-by-side field pairs share a combined error row.
   * Default `"floating"` = Onboarding floating-label style.
   */
  fieldLayout?: "floating" | "stacked";
};

// TODO Refactor the business details logic into a separate component
//  to keep this one's responsibilities small
export const PersonalDetailsFormFields: FC<PersonalDetailsFormFieldsProps> = ({
  disableAllFields = false,
  excludedFields = [],
  readonlyFields = [],
  addressHeading,
  organizationFieldsPosition = "bottom",
  organizationFieldsHeading,
  hideBusinessToggle = false,
  onBusinessSectionToggle,
  fieldLayout = "floating",
}: PersonalDetailsFormFieldsProps) => {
  const { t, i18n } = useTranslation(undefined, {
    keyPrefix: "Onboarding.sections.personal-details.form",
  });
  const baseLanguage = i18n.language.split("-")[0];
  const {
    watch,
    register,
    trigger,
    getValues,
    setValue,
    control,
    unregister,
    formState: { errors },
  } = useFormContext<BusinessPersonalDetailsFormFields>();

  const formOfAddress = watch(fk.formOfAddress);
  const selectedCountry = watch(fk.country);

  useEffect(() => {
    if (getValues(fk.zip)) {
      trigger(fk.zip);
    }
  }, [trigger, getValues, selectedCountry?.ALPHA2]);
  const { data: customerData } = useGetCustomer();
  const { dataContext } = useContext(DataContext);

  useEffect(() => {
    if (formOfAddress === null || formOfAddress === undefined) {
      const dataContextFormOfAddress = dataContext.personalDetails
        ?.formOfAddress
        ? String(dataContext.personalDetails.formOfAddress)
        : customerData?.salutation?.id?.toString() || "0";
      setValue(fk.formOfAddress, dataContextFormOfAddress);
    }
  }, [
    formOfAddress,
    setValue,
    dataContext.personalDetails?.formOfAddress,
    customerData?.salutation?.id,
  ]);

  // This might be undefined if we unregistered the business fields
  const organizationForm = watch(fk.organizationForm) as
    | LookupValue
    | undefined;
  const sortedCountries = useGetSortedCountries();
  const organizationIdIsRequired = useMemo(
    () =>
      organizationForm !== undefined &&
      organizationForm.id !== 0 &&
      doesRequireUIDCheck(organizationForm),
    [organizationForm],
  );

  // Private mode: org fields excluded by parent, checkbox toggles them on.
  // N/A when position="top" (caller already knows it's business).
  const isPrivateMode =
    organizationFieldsPosition === "bottom" &&
    !!excludedFields?.includes(fk.organizationName);
  const [showBusinessSection, setShowBusinessSection] = useState(false);
  const showOrgFields =
    organizationFieldsPosition === "top" ||
    !isPrivateMode ||
    showBusinessSection;

  useEffect(() => {
    if (showOrgFields) {
      if (organizationForm?.id !== 0) {
        trigger(fk.organizationId);
      }
    } else {
      unregister([fk.organizationName, fk.organizationForm, fk.organizationId]);
    }
  }, [trigger, unregister, organizationForm?.id, showOrgFields]);

  // In stacked mode, side-by-side field pairs share a single error row below
  // the grid so the narrow column (house number / zip) never clips its message.
  const stacked = fieldLayout === "stacked";

  const organizationFieldsBlock = showOrgFields && (
    <div className="organization-fields mt-2 flex flex-col">
      <FormField<BusinessPersonalDetailsFormFields>
        id="organization-name-input"
        label={t("label.organizationName")}
        name={fk.organizationName}
        errors={errors}
        register={register}
        type="text"
        layout={fieldLayout}
        placeholder={t("placeholder.organizationName")}
        options={{
          disabled: disableAllFields,
          readOnly: readonlyFields.includes(fk.organizationName),
          // Locked = always pre-filled, no need to require it
          required:
            !readonlyFields.includes(fk.organizationName) &&
            t("translation:label.validation.required"),
          // Locked field can't be edited to fix a pattern mismatch either
          pattern: readonlyFields.includes(fk.organizationName)
            ? undefined
            : {
                message: t(
                  "translation:Common.generalForm.error.genericRegexError",
                ),
                value: DEFAULT_TEXT_FIELD_REGEX,
              },
          setValueAs: (value: string) => value?.trim(),
        }}
      />
      <div>
        <Controller
          control={control}
          name={fk.organizationForm}
          rules={{
            required: !readonlyFields.includes(fk.organizationForm),
            validate: readonlyFields.includes(fk.organizationForm)
              ? undefined
              : (value) => value.id !== 0,
          }}
          render={({ field }) => (
            <Combobox<LookupValue>
              dataTestId="legalFormCombobox"
              field={field}
              label={t("label.organizationForm")}
              hasError={errors.organizationForm !== undefined}
              displayValue={(legalForm: LookupValue) => {
                return t(
                  `legalForms.${legalForm.id}`,
                  legalForm.description ?? "",
                );
              }}
              options={LEGAL_FORMS}
              placeholder={t("placeholder.organizationForm")}
              disabled={
                disableAllFields || readonlyFields.includes(fk.organizationForm)
              }
            />
          )}
        />
        {(errors.organizationForm?.type === "required" ||
          errors.organizationForm?.type === "validate") && (
          <p className="text-sm text-secondary-100">
            {t("translation:label.validation.required")}
          </p>
        )}
      </div>
      <FormField<BusinessPersonalDetailsFormFields>
        id="organization-id-input"
        label={t("label.organizationId")}
        name={fk.organizationId}
        errors={errors}
        register={register}
        type="text"
        layout={fieldLayout}
        placeholder={t("placeholder.organizationId")}
        options={{
          disabled: disableAllFields,
          readOnly: readonlyFields.includes(fk.organizationId),
          // Locked+empty is valid here, not every legal form needs an ID
          required:
            !readonlyFields.includes(fk.organizationId) &&
            organizationIdIsRequired &&
            t("translation:label.validation.required"),
          validate: readonlyFields.includes(fk.organizationId)
            ? undefined
            : (value: any) =>
                uidIsValid(value) || value === ""
                  ? true
                  : t("validation.organizationId"),
          onChange: (e: KeyboardEvent) => {
            const input = e.target as HTMLInputElement;
            const value = input.value.toUpperCase().replace(/[^a-zA-Z\d]/g, "");
            setValue(fk.organizationId, value);
          },
        }}
        hideOptionalLabel
      />
    </div>
  );

  return (
    <div id="personal-details-form-fields">
      <fieldset data-testid="form-of-address-group">
        {/* The redesign shows the salutation options with no visible caption above them —
            kept as a legend for a11y only. */}
        <legend className="sr-only">{t(`label.formOfAddress`)}</legend>

        <div
          className={clsx(
            "form-of-address",
            errors.formOfAddress?.type === "required" && "with-errors",
          )}
        >
          <Controller<BusinessPersonalDetailsFormFields>
            name={fk.formOfAddress}
            control={control}
            rules={{ required: !readonlyFields.includes(fk.formOfAddress) }}
            render={({ field: { value, onChange } }) => (
              <RadioGroup
                value={value}
                onChange={onChange}
                className="contents"
              >
                <RadioButtonOption<BusinessPersonalDetailsFormFields>
                  label={t("formOfAddress.mrs")}
                  name={fk.formOfAddress}
                  value="2"
                  disabled={
                    readonlyFields.includes(fk.formOfAddress) ||
                    disableAllFields
                  }
                />
                <RadioButtonOption<BusinessPersonalDetailsFormFields>
                  label={t("formOfAddress.mr")}
                  name={fk.formOfAddress}
                  value="1"
                  disabled={
                    readonlyFields.includes(fk.formOfAddress) ||
                    disableAllFields
                  }
                />
                <RadioButtonOption<BusinessPersonalDetailsFormFields>
                  label={t("formOfAddress.other")}
                  name={fk.formOfAddress}
                  value="0"
                  disabled={
                    readonlyFields.includes(fk.formOfAddress) ||
                    disableAllFields
                  }
                />
              </RadioGroup>
            )}
          />
        </div>
        <ErrorMessage
          name={fk.formOfAddress}
          message={
            errors.formOfAddress?.type === "required"
              ? t("translation:label.validation.required")
              : undefined
          }
        />
      </fieldset>
      <div className="names">
        <FormFieldWithHint<BusinessPersonalDetailsFormFields>
          id="first-name-input"
          label={
            readonlyFields.includes(fk.firstName)
              ? t("label.firstNameShort")
              : t("label.firstName")
          }
          name={fk.firstName}
          autocomplete="off"
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          hint={t("hint.firstName")}
          placeholder={t("placeholder.firstName")}
          options={{
            disabled: disableAllFields,
            readOnly: readonlyFields.includes(fk.firstName),
            required: t("translation:label.validation.required"),
            pattern: {
              value: INTERNATIONAL_NAME_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
          }}
        />
        <FormFieldWithHint<BusinessPersonalDetailsFormFields>
          id="last-name-input"
          label={
            readonlyFields.includes(fk.lastName)
              ? t("label.lastNameShort")
              : t("label.lastName")
          }
          name={fk.lastName}
          autocomplete="off"
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          hint={t("hint.lastName")}
          placeholder={t("placeholder.lastName")}
          options={{
            disabled: disableAllFields,
            required: t("translation:label.validation.required"),
            readOnly: readonlyFields.includes(fk.lastName),
            pattern: {
              value: INTERNATIONAL_NAME_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
      </div>
      {organizationFieldsPosition === "top" && (
        <>
          {organizationFieldsHeading}
          {organizationFieldsBlock}
        </>
      )}
      {addressHeading}
      <div className="street">
        <FormField<BusinessPersonalDetailsFormFields>
          id="street-input"
          label={t("label.street")}
          name={fk.street}
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          suppressError={stacked}
          placeholder={t(
            `placeholder.${showOrgFields ? "company-street" : fk.street}`,
          )}
          options={{
            disabled: disableAllFields,
            required: t("translation:label.validation.required"),
            readOnly: readonlyFields.includes(fk.street),
            maxLength: 40,
            pattern: {
              value: DACH_STREET_NAME_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
        <FormField<BusinessPersonalDetailsFormFields>
          id="house-number-input"
          label={t("label.houseNumber")}
          name={fk.houseNumber}
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          suppressError={stacked}
          placeholder={t("placeholder.houseNumber")}
          options={{
            disabled: disableAllFields,
            required: t("translation:label.validation.required"),
            readOnly: readonlyFields.includes(fk.houseNumber),
            maxLength: 10,
            pattern: {
              value: DACH_HOUSE_NUMBER_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
          }}
        />
        {stacked && (errors.street || errors.houseNumber) && (
          <div className="col-span-full flex flex-col gap-1">
            {errors.street?.message && (
              <ErrorMessage
                name={fk.street}
                message={errors.street.message as string}
              />
            )}
            {errors.houseNumber?.message && (
              <ErrorMessage
                name={fk.houseNumber}
                message={errors.houseNumber.message as string}
              />
            )}
          </div>
        )}
      </div>
      <div className="addressSupplement">
        <FormField<BusinessPersonalDetailsFormFields>
          id="address-supplement-input"
          label={t("label.addressSupplement")}
          name={fk.postalDeliveryNotes}
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          placeholder={t("placeholder.addressSupplement")}
          options={{
            disabled: disableAllFields,
            required: false,
            readOnly: readonlyFields.includes(fk.postalDeliveryNotes),
            maxLength: 40,
            pattern: {
              value: DEFAULT_TEXT_FIELD_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
      </div>
      <div className="zipAndCity">
        <FormField<BusinessPersonalDetailsFormFields>
          id="zip-code-input"
          label={t("label.zip")}
          name={fk.zip}
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          suppressError={stacked}
          placeholder={t("placeholder.zip")}
          options={{
            disabled: disableAllFields,
            required: t("translation:label.validation.required"),
            readOnly: readonlyFields.includes(fk.zip),
            maxLength: 10,
            pattern: {
              value: getPostalCodePattern(selectedCountry),
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
          }}
        />
        <FormField<BusinessPersonalDetailsFormFields>
          id="city-input"
          label={t("label.city")}
          name={fk.city}
          errors={errors}
          register={register}
          type="text"
          layout={fieldLayout}
          suppressError={stacked}
          placeholder={t("placeholder.city")}
          options={{
            disabled: disableAllFields,
            required: t("translation:label.validation.required"),
            readOnly: readonlyFields.includes(fk.city),
            maxLength: 40,
            pattern: {
              value: DEFAULT_TEXT_FIELD_REGEX,
              message: t(
                "translation:Common.generalForm.error.genericRegexError",
              ),
            },
            setValueAs: (value: string) => value?.trim(),
          }}
        />
        {stacked && (errors.zip || errors.city) && (
          <div className="col-span-full flex flex-col gap-1">
            {errors.zip?.message && (
              <ErrorMessage
                name={fk.zip}
                message={errors.zip.message as string}
              />
            )}
            {errors.city?.message && (
              <ErrorMessage
                name={fk.city}
                message={errors.city.message as string}
              />
            )}
          </div>
        )}
      </div>
      <div className="country">
        <Controller
          control={control}
          name={fk.country}
          defaultValue={SCHWEIZ}
          render={({ field }) => (
            <Combobox<Country>
              dataTestId="countryCombobox"
              field={field}
              label={t("label.country")}
              displayValue={(country?: Country) =>
                t(
                  country
                    ? `translation:countries.${country.ALPHA3}`
                    : "placeholder.country",
                )
              }
              options={sortedCountries}
              placeholder={t("placeholder.country")}
              disabled={disableAllFields || readonlyFields.includes(fk.country)}
            />
          )}
        />
      </div>
      <div
        className={clsx(
          !excludedFields.includes(fk.dateOfBirth) && "phoneAndDateOfBirth",
        )}
      >
        <PhoneInputField<BusinessPersonalDetailsFormFields>
          id="phone-number-input"
          label={t("label.phone")}
          name={fk.phone}
          control={control}
          errors={errors}
          disabled={disableAllFields}
          readOnly={readonlyFields.includes(PersonalDetailsFormFieldKeys.phone)}
          required={t("translation:label.validation.required")}
          validationMessage={t("translation:label.validation.phone")}
          backendError={t("translation:label.validation.phoneBackend")}
        />
        {!excludedFields.includes(fk.dateOfBirth) &&
          (readonlyFields.includes(PersonalDetailsFormFieldKeys.dateOfBirth) ? (
            <FormFieldWithHint<BusinessPersonalDetailsFormFields>
              id="date-of-birth-input"
              // Locked field: no required marker, unlike the editable
              // Onboarding label below which carries a "*"
              label={t("portal:user-account.personalDetails.dateOfBirth")}
              name={fk.dateOfBirth}
              errors={errors}
              layout={fieldLayout}
              // Fake register, no real ref: real register + value override
              // fights RHF's ref (overwrites DOM value), corrupting the
              // localized string on next render (day/month swap bug).
              register={
                (() => ({
                  name: fk.dateOfBirth,
                  onChange: async () => {},
                  onBlur: async () => {},
                  ref: () => {},
                })) as UseFormRegister<BusinessPersonalDetailsFormFields>
              }
              type="text"
              value={getLocalizedBirthday(
                getValues(fk.dateOfBirth),
                baseLanguage,
              )}
              placeholder={t("placeholder.dateOfBirth")}
              options={{ disabled: disableAllFields, readOnly: true }}
            />
          ) : (
            <DateInput<BusinessPersonalDetailsFormFields>
              id="birth-date-input"
              label={t("label.dateOfBirth")}
              name={fk.dateOfBirth}
              autocomplete="off"
              errors={errors}
              register={register}
              layout={fieldLayout}
              placeholder={t("placeholder.dateOfBirth")}
              value={watch(fk.dateOfBirth)}
              options={{
                disabled: disableAllFields,
                required: t("translation:label.validation.required"),
                validate: (value: string) => validateBirthDate({ value, t }),
              }}
            />
          ))}
      </div>

      {organizationFieldsPosition === "bottom" && (
        <>
          {/* Business account toggle — only in private mode */}
          {isPrivateMode && !hideBusinessToggle && (
            <label
              className={clsx(
                "mt-4 flex items-center gap-3",
                disableAllFields ? "cursor-default" : "cursor-pointer",
              )}
            >
              <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
                <input
                  className="peer absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-default"
                  type="checkbox"
                  checked={showBusinessSection}
                  disabled={disableAllFields}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (
                      onBusinessSectionToggle &&
                      onBusinessSectionToggle(checked) === false
                    ) {
                      return;
                    }
                    setShowBusinessSection(checked);
                  }}
                />
                <span
                  className={clsx(
                    "pointer-events-none absolute inset-0 rounded-md border-2 border-border-secondary text-foreground-primary-inverse transition-colors duration-150",
                    "peer-checked:border-0 peer-checked:bg-surface-primary",
                    "peer-[:is(:hover,:focus-visible):not(:checked)]:border-border-primary",
                    "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-foreground-primary",
                    "peer-disabled:border-border-tertiary",
                  )}
                >
                  {showBusinessSection && <SelectedCheckbox />}
                </span>
              </span>
              <span className="text-base font-semibold text-foreground-primary">
                {t("label.isBusinessAccount")}
              </span>
            </label>
          )}

          {/* Business fields — shown in business tab or when checkbox is checked */}
          {organizationFieldsBlock}
        </>
      )}
    </div>
  );
};
